// ============================================================
// PLUMME — Delt godkjenningslag for assistenthandlinger
// ============================================================
// Plattform-routeren er eneste beslutningsmotor, og skal ha ett godkjenningsløp
// uansett hvor brukeren kom fra. Hjem-assistenten og AI Rådgiver bruker derfor
// de samme hjelperne her i stedet for hver sin kopi — ellers ville to innganger
// til samme handling fått lov til å drive fra hverandre på hva som krever
// bekreftelse og hvor payloaden lander.
//
// Merk hva som IKKE ligger her: selve utførelsen. Med unntak av `register_payment`
// åpner vi riktig side med payloaden ferdig utfylt og lar brukeren fullføre der.
// Rådgiveren er en inngang til godkjenningen, ikke et parallelt executor-lag.
const PlummeAssistantActions = (() => {
  const asRecord = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : null);

  // En pending flyt finnes når routeren enten mangler felter, har en payload, eller
  // eksplisitt ber om bekreftelse. Uten dette ville oppfølgingsspørsmål mistet
  // konteksten og startet forfra.
  const pendingFromReply = (metadata) => {
    const meta = asRecord(metadata);
    if (!meta) return null;
    const intentName = typeof meta.intentName === 'string' ? meta.intentName : '';
    const missingFields = Array.isArray(meta.missingFields) ? meta.missingFields.filter(Boolean) : [];
    const executionPayload = asRecord(meta.executionPayload) || undefined;
    if (!intentName || (!missingFields.length && !executionPayload && !meta.requiresConfirmation)) return null;
    return { intentName, missingFields, executionPayload };
  };

  // En handling vises bare når routeren både krever bekreftelse OG har markert
  // payloaden klar. Begge deler: `readyForExecution` alene kan stå på en payload
  // som fortsatt venter på opplysninger.
  const actionFromReply = (metadata) => {
    const meta = asRecord(metadata);
    const payload = asRecord(meta?.executionPayload);
    return meta?.requiresConfirmation && payload?.readyForExecution ? payload : null;
  };

  const ROUTE_BY_ACTION = {
    create_invoice: 'faktura',
    create_other_sale: 'annetsalg',
    create_expense: 'nyttkjop',
    register_purchase: 'nyttkjop',
    create_customer: 'kunder',
    create_supplier: 'leverandorer',
    create_product: 'produkter',
    suggest_accounting_entry: 'fripostering',
    create_mileage_entry: 'kjorebok',
    send_sales_document: 'fakturaliste',
    // Purring håndteres på fakturalista, der mottaker og beløp kan kontrolleres
    // per faktura før noe sendes.
    send_payment_reminder: 'fakturaliste',
    send_reminder: 'fakturaliste',
    register_payment: 'fakturaliste',
    book_bank_lines: 'bank',
    link_bank_receipts: 'bank',
    annul_bank_posting: 'bank',
    reporting_preparation: 'innsendinger',
  };
  const routeForAction = (payload) => ROUTE_BY_ACTION[asRecord(payload)?.action] || null;

  const acmText = (key, fallback) => globalThis.PlummeI18n?.t?.(key, fallback) || fallback;

  const executeApprovedAction = async (rawPayload) => {
    const payload = asRecord(rawPayload);
    const route = routeForAction(payload);
    if (!payload || !route) throw new Error(acmText('acm.noAdapter', 'Assistenthandlingen har ingen godkjent adapter.'));
    globalThis.__plummeAssistantActionPayload = payload;
    if (payload.action === 'create_invoice') {
      const result = await globalThis.PlummeBackend?.createAssistantInvoiceDraft?.(payload);
      if (!result?.invoice) throw new Error(acmText('acm.invoiceDraftFailed', 'Fakturautkastet kunne ikke opprettes.'));
      globalThis.PlummeBackend?.setOpenInvoice?.(result.invoice);
      return { route, result, mutation: 'invoice_draft_created' };
    }
    if (payload.action === 'register_payment') {
      const result = await globalThis.PlummeBackend?.registerAssistantPayment?.(payload);
      if (!result?.payment) throw new Error(acmText('acm.paymentFailed', 'Betalingen kunne ikke registreres.'));
      return { route, result, mutation: 'invoice_payment_registered' };
    }
    if (payload.action === 'create_customer') {
      const customer = asRecord(payload.normalizedCustomer);
      if (!customer?.name || !globalThis.PlummeBackend?.saveCustomerRecord) throw new Error(acmText('acm.customerSaveFailed', 'Kunden kunne ikke lagres.'));
      const result = await globalThis.PlummeBackend.saveCustomerRecord({
        name: customer.name,
        org: customer.organizationNumber || '',
        email: customer.email || '',
        phone: customer.phone || '',
        addr: customer.address || '',
        zip: customer.postalCode || '',
        city: customer.postalPlace || '',
        status: 'aktiv',
      });
      if (!result?.id) throw new Error(acmText('acm.customerSaveFailed', 'Kunden kunne ikke lagres.'));
      return { route, result, mutation: 'customer_created' };
    }
    if (payload.action === 'create_supplier') {
      const supplier = asRecord(payload.normalizedSupplier);
      if (!supplier?.name || !globalThis.PlummeBackend?.saveSupplierRecord) throw new Error(acmText('acm.supplierSaveFailed', 'Leverandøren kunne ikke lagres.'));
      const result = await globalThis.PlummeBackend.saveSupplierRecord({
        name: supplier.name,
        org: supplier.organizationNumber || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        addr: supplier.address || '',
        zip: supplier.postalCode || '',
        city: supplier.postalPlace || '',
        status: 'aktiv',
      });
      if (!result?.id) throw new Error(acmText('acm.supplierSaveFailed', 'Leverandøren kunne ikke lagres.'));
      return { route, result, mutation: 'supplier_created' };
    }
    if (payload.action === 'create_product') {
      const product = asRecord(payload.normalizedProduct);
      if (!product?.name || !(Number(product.unitPrice) > 0) || !globalThis.PlummeBackend?.saveProductRecord) {
        throw new Error(acmText('acm.productSaveFailed', 'Produktet kunne ikke lagres.'));
      }
      const result = await globalThis.PlummeBackend.saveProductRecord({
        name: product.name,
        desc: product.description || product.name,
        price: Number(product.unitPrice),
        vat: Number(product.vatRate) || 0,
        vatSelection: globalThis.PlummeBackend?.encodeSalesVatSelection?.(product.vatTreatment, product.vatRate)
          || (product.vatTreatment && product.vatTreatment !== 'standard' ? product.vatTreatment : String(Number(product.vatRate) || 0)),
        vatTreatment: product.vatTreatment || (Number(product.vatRate) > 0 ? 'standard' : 'zeroRated'),
        type: product.productType === 'product' ? 'Vare' : 'Tjeneste',
        salesAccountCode: product.salesAccountCode || undefined,
        status: 'aktiv',
      });
      if (!result?.id) throw new Error(acmText('acm.productSaveFailed', 'Produktet kunne ikke lagres.'));
      return { route, result, mutation: 'product_created' };
    }
    if ((payload.action === 'create_expense' || payload.action === 'register_purchase') && payload.receipt) {
      globalThis.__plummeRouteReceipt = payload.receipt;
    }
    return { route, result: null, mutation: null };
  };

  const actionLabel = (payload) => {
    const action = asRecord(payload)?.action || '';
    return action && globalThis.PlummeI18n?.has?.('home.action.' + action)
      ? globalThis.PlummeI18n.t('home.action.' + action)
      : globalThis.PlummeI18n?.t?.('home.action.default') || 'Fortsett';
  };

  return { pendingFromReply, actionFromReply, routeForAction, executeApprovedAction, actionLabel, ROUTE_BY_ACTION };
})();

if (typeof globalThis !== 'undefined') globalThis.PlummeAssistantActions = PlummeAssistantActions;
if (typeof module !== 'undefined' && module.exports) module.exports = PlummeAssistantActions;
